import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { showApiErrorToast } from '@/utils/errors';
import { confirmBatch, disburseBatch, publishBatch, publishQueryKeys } from '../api/publish.api';
import type { PublishPayload } from '../types/publish.types';

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: publishQueryKeys.all() });
  };
}

export function usePublishBatch(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (payload: PublishPayload) => publishBatch(payload),
    onSuccess: () => {
      invalidate();
      toast.success('Published');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to publish'),
  });
}

export function useConfirmBatch(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      batchId,
      isConfirm,
      comments,
    }: {
      batchId: number;
      isConfirm: boolean;
      comments?: string;
    }) => confirmBatch(batchId, isConfirm, comments),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(
        variables.isConfirm
          ? 'Batch confirmed'
          : 'Batch sent back - its work is in the publish queue again',
      );
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to update the batch'),
  });
}

export function useDisburseBatch(onSuccess?: () => void) {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ batchId, comments }: { batchId: number; comments?: string }) =>
      disburseBatch(batchId, comments),
    onSuccess: () => {
      invalidate();
      toast.success('Batch disbursed');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to disburse'),
  });
}
